import { createContext, useContext, useEffect, useState } from 'react'
import type { PropsWithChildren } from 'react'

interface DebugModeState {
  isDebug: boolean
  debugParam: Record<string, string> | false
}

const DebugModeStateContext = createContext<DebugModeState>(
  {} as DebugModeState,
)
DebugModeStateContext.displayName = 'DebugModeState'

const debugParamKeysForCheck = ['debug', 'isDebug']
const checkDebugMode = (): Record<string, string> | false => {
  const filteredEntries = Array.from(
    new URLSearchParams(window.location.search).entries(),
  ).filter((e) => debugParamKeysForCheck.includes(e[0]))

  return filteredEntries.length > 0
    ? Object.fromEntries(filteredEntries)
    : false
}

function DebugModeProvider({ children }: PropsWithChildren): JSX.Element {
  const [debugParam, setDebugParam] = useState<Record<string, string> | false>(
    checkDebugMode(),
  )

  useEffect(() => {
    const handlePopState = (): void => {
      setDebugParam(checkDebugMode())
    }

    window.addEventListener('popstate', handlePopState)

    return (): void => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  return (
    <DebugModeStateContext.Provider
      value={{ isDebug: !!debugParam, debugParam }}
    >
      {children}
    </DebugModeStateContext.Provider>
  )
}

function useDebugMode(): DebugModeState {
  try {
    const context = useContext(DebugModeStateContext)
    return context
  } catch (error) {
    throw new Error('useDebugModeState must be used within a DebugModeProvider')
  }
}

export { useDebugMode, DebugModeProvider }
